import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Shield, Settings, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

// Helper to set a cookie
const setCookie = (name, value, days = 365) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
};

// Helper to get a cookie
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
  return null;
};

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check both localStorage and cookie for consent
    const localStorageConsent = localStorage.getItem('squidemy_cookie_consent');
    const cookieConsent = getCookie('squidemy_cookie_consent');
    
    // Only show banner if neither exists
    if (!localStorageConsent && !cookieConsent) {
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consentData = JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
      timestamp: new Date().toISOString()
    });
    // Store in both localStorage and cookie for persistence
    localStorage.setItem('squidemy_cookie_consent', consentData);
    setCookie('squidemy_cookie_consent', 'accepted', 365);
    setShowBanner(false);
  };

  const handleAcceptRequired = () => {
    const consentData = JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
      timestamp: new Date().toISOString()
    });
    // Store in both localStorage and cookie for persistence
    localStorage.setItem('squidemy_cookie_consent', consentData);
    setCookie('squidemy_cookie_consent', 'required_only', 365);
    setShowBanner(false);
  };

  const handleClose = () => {
    handleAcceptAll();
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          key="cookie-consent-banner"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-0 left-0 right-0 z-[9999] bg-slate-900/98 backdrop-blur-lg border-t border-slate-700 shadow-2xl"
        >
          {/* Main Banner Content */}
          <div className="w-full px-4 md:px-8 py-4">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
              {/* Left Side - Cookie Icon and Text */}
              <div className="flex items-start gap-4 flex-1">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shrink-0">
                  <Cookie className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-slate-200 text-sm leading-relaxed">
                    Our website requires some cookies to function properly. Other cookies may be used with your consent to analyze site usage, improve the user experience, and for advertising.{' '}
                    <button 
                      onClick={() => setShowDetails(!showDetails)}
                      className="text-purple-400 hover:text-purple-300 transition-colors underline inline-flex items-center gap-1"
                    >
                      {showDetails ? 'Hide details' : 'Show details'}
                      {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                  </p>
                  <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-400">
                    <Link to="/squidcookies" className="hover:text-white transition-colors underline">
                      Cookie Policy
                    </Link>
                    <span className="text-slate-600">|</span>
                    <Link to="/squidprivacy" className="hover:text-white transition-colors underline">
                      Privacy Statement
                    </Link>
                  </div>
                </div>
              </div>

              {/* Right Side - Buttons */}
              <div className="flex items-center gap-3 shrink-0">
                <Button
                  onClick={handleAcceptRequired}
                  variant="outline"
                  size="sm"
                  className="border-slate-600 hover:bg-slate-800 text-slate-300"
                >
                  Accept Required Only
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  size="sm"
                  className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
                >
                  Accept All Cookies
                </Button>
                <button 
                  onClick={handleClose}
                  className="p-2 hover:bg-slate-800 rounded-lg transition-colors ml-2"
                  aria-label="Close cookie banner"
                >
                  <X className="w-5 h-5 text-slate-400 hover:text-white" />
                </button>
              </div>
            </div>
          </div>

          {/* Expandable Details Section */}
          <AnimatePresence>
            {showDetails && (
              <motion.div 
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden border-t border-slate-700/50"
              >
                <div className="w-full px-4 md:px-8 py-4 bg-slate-800/50">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-6xl">
                    {/* Necessary Cookies */}
                    <div className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg">
                      <Shield className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-white">Necessary</span>
                          <span className="text-xs text-green-400 bg-green-400/10 px-2 py-0.5 rounded">Always Active</span>
                        </div>
                        <p className="text-xs text-slate-400">Essential for the website to function properly.</p>
                      </div>
                    </div>

                    {/* Analytics Cookies */}
                    <div className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg">
                      <Settings className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-white">Analytics</span>
                          <span className="text-xs text-slate-500 bg-slate-700 px-2 py-0.5 rounded">Optional</span>
                        </div>
                        <p className="text-xs text-slate-400">Help us understand how visitors interact with our website.</p>
                      </div>
                    </div>

                    {/* Marketing Cookies */}
                    <div className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg">
                      <Cookie className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-white">Marketing</span>
                          <span className="text-xs text-slate-500 bg-slate-700 px-2 py-0.5 rounded">Optional</span>
                        </div>
                        <p className="text-xs text-slate-400">Used to deliver relevant ads and track campaign performance.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
